/**
 * ClergyTransfers — moving a pastor from one church to another.
 * HQ decides; approving carries the membership, the leadership post and the
 * admin access scope across together.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowRight, ArrowLeftRight, Check, Loader2, Mail, Plus, UserRound, X,
} from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  declined: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_LABELS = {
  pending: 'Awaiting HQ', approved: 'Approved', declined: 'Declined', cancelled: 'Cancelled',
};

const NewPostingDialog = ({ open, onOpenChange }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    member_id: '', to_church_id: '', post_title: '',
    install_as_leader: true, move_admin_scope: true, reason: '',
  });

  const { data: options, isLoading } = useQuery({
    queryKey: ['clergy-transfer-options'],
    queryFn: async () => (await api.get('/admin/clergy-transfers/options')).data,
    enabled: open,
  });

  React.useEffect(() => {
    if (open) setForm({ member_id: '', to_church_id: '', post_title: '',
                        install_as_leader: true, move_admin_scope: true, reason: '' });
  }, [open]);

  const pastor = useMemo(
    () => (options?.clergy || []).find((p) => p.id === form.member_id),
    [options, form.member_id]);

  const create = useMutation({
    mutationFn: async () => (await api.post('/admin/clergy-transfers', form)).data,
    onSuccess: () => {
      toast.success('Posting request raised');
      queryClient.invalidateQueries({ queryKey: ['clergy-transfers'] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="clergy-transfer-dialog">
        <DialogHeader>
          <DialogTitle>Post a pastor to another church</DialogTitle>
          <DialogDescription>
            Approving this moves their membership, their leadership post and their
            admin access scope in one go.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? <Skeleton className="h-40 w-full" /> : (
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label className="text-xs">Pastor *</Label>
              <Select value={form.member_id}
                      onValueChange={(v) => setForm({ ...form, member_id: v })}>
                <SelectTrigger data-testid="clergy-pastor-select">
                  <SelectValue placeholder="Pick a pastor or leader" />
                </SelectTrigger>
                <SelectContent>
                  {(options?.clergy || []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.role ? ` · ${p.role}` : ''}
                      {p.holds_seat_at ? ` · ${p.holds_seat_at}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(options?.clergy || []).length === 0 && (
                <p className="text-xs text-slate-500" data-testid="clergy-none">
                  No pastors or seat-holders are recorded yet — give a member a clergy
                  role or make them a leader on the tree first.
                </p>
              )}
            </div>

            {pastor && (
              <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600">
                {pastor.email || 'no email on file'}
                {pastor.holds_seat_at && <> · currently holds the seat at <b>{pastor.holds_seat_at}</b></>}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Receiving church *</Label>
              <Select value={form.to_church_id}
                      onValueChange={(v) => setForm({ ...form, to_church_id: v })}>
                <SelectTrigger data-testid="clergy-church-select">
                  <SelectValue placeholder="Where are they going?" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(options?.churches || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.city ? ` · ${c.city}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Title at the new church</Label>
              <Input value={form.post_title} data-testid="clergy-title-input"
                     onChange={(e) => setForm({ ...form, post_title: e.target.value })}
                     placeholder="Leave blank to use the default for that level" />
            </div>

            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              <label className="flex items-center justify-between gap-4 text-sm text-slate-700">
                Install them as the leader there
                <Switch checked={form.install_as_leader} data-testid="clergy-install-toggle"
                        onCheckedChange={(v) => setForm({ ...form, install_as_leader: v })} />
              </label>
              <label className="flex items-center justify-between gap-4 text-sm text-slate-700">
                Move their admin access scope
                <Switch checked={form.move_admin_scope} data-testid="clergy-scope-toggle"
                        onCheckedChange={(v) => setForm({ ...form, move_admin_scope: v })} />
              </label>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Why</Label>
              <Textarea rows={3} value={form.reason} data-testid="clergy-reason-input"
                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        placeholder="Planting, reassignment, relocation…" />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()} data-testid="clergy-submit-btn"
                  disabled={!form.member_id || !form.to_church_id || create.isPending}>
            {create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Raise posting
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PostingRow = ({ row }) => {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  const act = useMutation({
    mutationFn: async ({ path, approve }) => (await api.post(
      `/admin/clergy-transfers/${row.id}/${path}`,
      path === 'cancel' ? {} : { approve, note })).data,
    onSuccess: (res) => {
      toast.success(res.status === 'approved' ? 'Posting applied' : 'Decision recorded');
      queryClient.invalidateQueries({ queryKey: ['clergy-transfers'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pending = row.status === 'pending';
  const sentTo = (row.notifications || []).filter((n) => n.sent).length;

  return (
    <Card data-testid={`clergy-row-${row.id}`}>
      <CardContent className="py-4 space-y-3">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <UserRound className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-semibold text-slate-900">{row.member_name}</span>
              <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[row.status] || ''}`}
                     data-testid={`clergy-status-${row.id}`}>
                {STATUS_LABELS[row.status] || row.status}
              </Badge>
              {row.move_admin_scope && (
                <Badge variant="secondary" className="text-[10px]">access moves too</Badge>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-400">{row.from_church_name || 'unposted'}</span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-medium text-slate-800">{row.to_church_name}</span>
              {(row.applied?.post_title || row.post_title) && (
                <span className="text-slate-500">· {row.applied?.post_title || row.post_title}</span>
              )}
            </div>
            {row.reason && <p className="mt-2 text-xs text-slate-500">“{row.reason}”</p>}
            {row.status !== 'pending' && (row.notifications || []).length > 0 && (
              <p className="mt-2 text-xs text-slate-400 flex items-center gap-1.5"
                 data-testid={`clergy-notices-${row.id}`}>
                <Mail className="h-3 w-3" />
                {sentTo > 0
                  ? `${sentTo} of ${row.notifications.length} notices sent`
                  : `Notices recorded but not sent — ${row.notifications[0]?.reason || 'email is off'}`}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {pending && (
              <>
                <Button size="sm" data-testid={`clergy-approve-${row.id}`}
                        onClick={() => act.mutate({ path: 'decision', approve: true })}>
                  <Check className="h-3.5 w-3.5 mr-1.5" /> Approve and post
                </Button>
                <Button size="sm" variant="outline" data-testid={`clergy-decline-${row.id}`}
                        onClick={() => act.mutate({ path: 'decision', approve: false })}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Decline
                </Button>
                <Button size="sm" variant="ghost" data-testid={`clergy-cancel-${row.id}`}
                        onClick={() => act.mutate({ path: 'cancel' })}>Cancel</Button>
              </>
            )}
            <Button size="sm" variant="ghost" data-testid={`clergy-history-${row.id}`}
                    onClick={() => setOpen((v) => !v)}>{open ? 'Hide history' : 'History'}</Button>
          </div>
        </div>
        {pending && (
          <Input value={note} onChange={(e) => setNote(e.target.value)}
                 data-testid={`clergy-note-${row.id}`}
                 placeholder="Add a note to your decision (optional)" className="text-sm" />
        )}
        {open && (
          <div className="border-t border-slate-100 pt-3 space-y-1.5">
            {(row.history || []).map((h, i) => (
              <p key={i} className="text-xs text-slate-500">
                <span className="font-mono text-slate-400">{new Date(h.at).toLocaleString()}</span>
                {' '}— {h.action} by {h.by}{h.note ? ` · “${h.note}”` : ''}
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ClergyTransfers = () => {
  const [filter, setFilter] = useState('pending');
  const [dialog, setDialog] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['clergy-transfers', filter],
    queryFn: async () => (await api.get(
      `/admin/clergy-transfers${filter === 'all' ? '' : `?status=${filter}`}`)).data,
    retry: false,
  });

  if (error) {
    return (
      <Card><CardContent className="py-12 text-center" data-testid="clergy-forbidden">
        <p className="text-sm text-slate-500">Only HQ (platform super admins) can post clergy.</p>
      </CardContent></Card>
    );
  }
  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const rows = data?.transfers || [];
  const counts = data?.counts || {};

  return (
    <div className="space-y-6" data-testid="clergy-transfers">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-indigo-500" /> Clergy postings
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            HQ approves every posting. Membership, the leadership seat and the admin
            access scope all move together.
          </p>
        </div>
        <Button onClick={() => setDialog(true)} data-testid="clergy-new-btn">
          <Plus className="h-4 w-4 mr-2" /> Post a pastor
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[['pending', `Awaiting HQ (${counts.pending || 0})`],
          ['approved', `Approved (${counts.approved || 0})`],
          ['declined', `Declined (${counts.declined || 0})`],
          ['all', 'Everything']].map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)}
                    data-testid={`clergy-filter-${key}`}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors
                      ${filter === key ? 'bg-slate-900 text-white border-slate-900'
                                       : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
              {label}
            </button>
          ))}
      </div>

      {rows.length === 0 ? (
        <Card><CardContent className="py-14 text-center" data-testid="clergy-empty">
          <ArrowLeftRight className="h-8 w-8 mx-auto text-slate-300" />
          <p className="mt-4 text-sm text-slate-500">No postings here yet.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">{rows.map((r) => <PostingRow key={r.id} row={r} />)}</div>
      )}

      <NewPostingDialog open={dialog} onOpenChange={setDialog} />
    </div>
  );
};

export default ClergyTransfers;
